import { styled } from '@mui/material/styles';
import { Box, List, ListItem, ListItemText, Typography, ListItemIcon } from '@mui/material';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import NotificationsIcon from '@mui/icons-material/Notifications';
import SwapHorizIcon from '@mui/icons-material/SwapHoriz';
import ArrowForwardIosIcon from '@mui/icons-material/ArrowForwardIos';

// ============================================================================
// MAIN CONTAINER COMPONENTS
// ============================================================================

export const NotificationContainer = styled(Box)(({ theme }) => ({
    backgroundColor: theme.palette.background.paper,
    borderRadius: '20px',
    boxShadow: theme.shadows[8],
    width: '400px',
    maxHeight: '600px',
    display: 'flex',
    flexDirection: 'column',
    border: `1px solid ${theme.palette.divider}`,
}));

export const NotificationHeader = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    borderBottom: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    borderTopLeftRadius: '12px',
    borderTopRightRadius: '12px',

    '& .MuiIconButton-root': {
        padding: '8px',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}));

export const StyledBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    borderBottom: `1px solid ${theme.palette.divider}`,
    borderTop: `1px solid ${theme.palette.divider}`,
}));

export const LoadMoreButton = styled(Box)(({ theme }) => ({
    padding: theme.spacing(1),
    borderTop: `1px solid ${theme.palette.divider}`,
    backgroundColor: theme.palette.background.default,
    borderBottomLeftRadius: '12px',
    borderBottomRightRadius: '12px',

    '& .MuiButton-root': {
        textTransform: 'none',
        fontWeight: 500,
        fontSize: '0.9rem',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}));

// ============================================================================
// LIST AND LIST ITEMS
// ============================================================================

export const NotificationList = styled(List)(({ theme }) => ({
    overflowY: 'auto',
    maxHeight: '500px',
    padding: 0,

    // List item styles
    '& .MuiListItem-root': {
        padding: theme.spacing(2),
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },

    // List item icon styles
    '& .MuiListItemIcon-root': {
        minWidth: '40px',
        '& .MuiSvgIcon-root': {
            fontSize: '1.25rem',
            color: theme.palette.text.secondary,
        },
    },

    // List item text styles
    '& .MuiListItemText-primary': {
        fontSize: '0.95rem',
        marginBottom: '4px',
    },
    '& .MuiListItemText-secondary': {
        fontSize: '0.8rem',
        color: theme.palette.text.secondary,
    },

    // Divider styles
    '& .MuiDivider-root': {
        margin: '0',
    },

    // Custom scrollbar
    '&::-webkit-scrollbar': {
        width: '8px',
    },
    '&::-webkit-scrollbar-track': {
        background: theme.palette.background.default,
    },
    '&::-webkit-scrollbar-thumb': {
        background: theme.palette.divider,
        borderRadius: '4px',
    },
}));

export const StyledListItem = styled(ListItem, {
    shouldForwardProp: (prop) => prop !== 'isRead',
})(({ theme, isRead }) => ({
    cursor: 'pointer',
    backgroundColor: isRead ? theme.palette.action.selected : 'transparent',
    borderRadius: '8px',
    margin: '4px 8px',
    width: 'auto',
}));

export const StyledListItemText = styled(ListItemText)(({ theme, isRead }) => ({
    '& .MuiListItemText-primary': {
        fontWeight: isRead ? 'normal' : 600,
        color: theme.palette.text.primary,
    },
}));

// ============================================================================
// SHIFT SWAP SPECIFIC COMPONENTS
// ============================================================================

export const ShiftSwapListItem = styled(StyledListItem)(({ theme }) => ({
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    borderRadius: 16,
    margin: theme.spacing(1),
    transition: 'all 0.2s ease-in-out',
    cursor: 'pointer',

    '&:hover': {
        background: 'linear-gradient(135deg, #5a6fd8 0%, #6a4190 100%)',
        transform: 'translateY(-1px)',
        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.4)',
    },
}));

export const ShiftSwapIcon = styled(SwapHorizIcon)({
    color: 'white !important',
    '& path, & circle': {
        fill: 'white !important'
    },
});

export const ShiftSwapListItemIcon = styled(ListItemIcon)({
    color: 'white',
    minWidth: 40,
});

export const ShiftSwapTitle = styled(Typography)({
    fontWeight: 600,
    color: 'white',
});

export const ShiftSwapMessage = styled(Typography)(({ theme }) => ({
    color: 'rgba(255,255,255,0.9)',
    marginBottom: theme.spacing(0.5),
}));

export const ShiftSwapTime = styled(Typography)({
    color: 'rgba(255,255,255,0.7)',
});

export const ShiftSwapActionBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: theme.spacing(1),
    alignSelf: 'center',
}));

export const ShiftSwapActionText = styled(Typography)(({ theme }) => ({
    color: 'rgba(255,255,255,0.8)',
    marginRight: theme.spacing(1),
}));

export const ShiftSwapArrowIcon = styled(ArrowForwardIosIcon)({
    fontSize: 14,
    color: 'rgba(255,255,255,0.8)',
});

// ============================================================================
// UTILITY COMPONENTS
// ============================================================================

export const EmptyNotificationsBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(4),
    textAlign: 'center',
    color: theme.palette.text.secondary,
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
}));

export const LoadingBox = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    padding: theme.spacing(4),
}));

export const StyledNotificationIcon = styled(NotificationsIcon)({
    fontSize: 40,
    opacity: 0.5,
});

export const StyledUnreadIndicator = styled(FiberManualRecordIcon)(({ theme }) => ({
    fontSize: 8,
    marginLeft: theme.spacing(1),
    color: theme.colors.white,
    alignSelf: 'center',
}));

export const MarkAllAsReadButton = styled(Box)(({ theme }) => ({
    display: 'flex',
    gap: theme.spacing(1),
    alignItems: 'center',

    '& .MuiIconButton-root': {
        padding: '8px',
        '&:hover': {
            backgroundColor: theme.palette.action.hover,
        },
    },
}));

export const ErrorDisplayBox = styled(Box)(({ theme }) => ({
    padding: theme.spacing(2),
    backgroundColor: theme.palette.error.light,
    color: theme.palette.error.contrastText,
    textAlign: 'center',
}));